import { formatDate } from '@angular/common';
import { inject, Injectable, provideAppInitializer } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatDateFormats,
  NativeDateAdapter,
} from '@angular/material/core';
import { TranslocoService } from '@jsverse/transloco';
import { parse as parseDate } from 'date-fns';
import { distinctUntilChanged, map } from 'rxjs';
import { getLanguageLocale, LANGUAGE_KEY } from '../constants/languages.const';
import { getDateInputFormatFromLocale } from '../helpers/date-format.helper';

const C_MaterialDateFormats: MatDateFormats = {
  parse: {
    dateInput: 'shortDate',
    timeInput: null,
  },
  display: {
    dateInput: 'shortDate',
    timeInput: 'shortTime',
    monthYearLabel: 'LLL yyyy',
    dateA11yLabel: 'longDate',
    monthYearA11yLabel: 'MMMM yyyy',
    timeOptionLabel: 'shortTime',
  },
};

@Injectable()
class MondayDateAdapter extends NativeDateAdapter {
  private get dateInputFormat(): string {
    return getDateInputFormatFromLocale(this.locale);
  }

  // Use Angular's bundled locale data because browser Intl may override the requested locale.
  override getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    const pattern = { long: 'LLLL', short: 'LLL', narrow: 'LLLLL' }[style];
    return Array.from({ length: 12 }, (_, month) => formatDate(new Date(2017, month, 1), pattern, this.locale));
  }

  override getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    const pattern = { long: 'cccc', short: 'ccc', narrow: 'ccccc' }[style];
    return Array.from({ length: 7 }, (_, day) => formatDate(new Date(2017, 0, day + 1), pattern, this.locale));
  }

  override getDateNames(): string[] {
    return Array.from({ length: 31 }, (_, day) => String(day + 1));
  }

  override getYearName(date: Date): string {
    return formatDate(date, 'y', this.locale);
  }

  override getFirstDayOfWeek(): number {
    return 1; // Monday
  }

  override format(date: Date, displayFormat: string): string {
    if (!this.isValid(date)) {
      throw new Error('Cannot format an invalid date.');
    }
    return formatDate(date, displayFormat === 'shortDate' ? this.dateInputFormat : displayFormat, this.locale);
  }

  override parse(value: unknown, parseFormat?: unknown): Date | null {
    if (typeof value === 'string' && /^\d{1,2}[./]\d{1,2}[./]\d{4}$/.test(value.trim())) {
      return parseDate(value.trim(), this.dateInputFormat, this.today());
    }
    return super.parse(value, parseFormat);
  }
}

export function provideMaterialDateConfig() {
  return [
    { provide: DateAdapter, useClass: MondayDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: C_MaterialDateFormats },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: () => getLanguageLocale(localStorage.getItem(LANGUAGE_KEY)),
    },
    provideAppInitializer(() => {
      const dateAdapter = inject<DateAdapter<Date>>(DateAdapter);
      inject(TranslocoService)
        .langChanges$.pipe(map(getLanguageLocale), distinctUntilChanged(), takeUntilDestroyed())
        .subscribe((locale) => dateAdapter.setLocale(locale));
    }),
  ];
}
